import React, { useEffect, useState } from 'react';
import { Row, Col, Card, ButtonGroup, Button, Table, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import constants from '../../const.js';

export const RANGES = [7, 30, 90, 365, 'all'];

export const formatDuration = (seconds) => {
    const s = Math.round(Number(seconds) || 0);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
};

export const scenePoster = (id) =>
    `${constants.TRAILER_CDN}sus-media/scenes/${String(id).padStart(4, '0')}/preview.webp`;

export const Kpi = ({ label, value, sub }) => (
    <Card className="h-100 text-center shadow-sm">
        <Card.Body className="py-3">
            <div className="text-muted small text-uppercase">{label}</div>
            <div className="h3 mb-0 font-weight-bold">{value}</div>
            {sub ? <div className="text-muted small">{sub}</div> : null}
        </Card.Body>
    </Card>
);

export const BarChart = ({ data, labelKey, valueKey, height = 180, formatLabel, xLabel }) => {
    const max = Math.max(1, ...data.map(d => Number(d[valueKey]) || 0));
    const vertical = data.length > 35;
    const labelSpace = vertical ? 32 : 16;
    return (
        <div>
            <div className="d-flex align-items-end" style={{ height, gap: 2 }}>
                {data.map(d => {
                    const v = Number(d[valueKey]) || 0;
                    return (
                        <div key={d[labelKey]} className="flex-fill d-flex flex-column justify-content-end align-items-center h-100"
                            style={{ minWidth: 0 }} title={`${formatLabel ? formatLabel(d[labelKey]) : d[labelKey]}: ${v}`}>
                            <div className="text-muted" style={{
                                fontSize: vertical ? 9 : 11, lineHeight: 1, marginBottom: 2, whiteSpace: 'nowrap',
                                ...(vertical ? { writingMode: 'vertical-rl', transform: 'rotate(180deg)' } : {})
                            }}>
                                {v || ''}
                            </div>
                            <div className="bg-primary rounded-top w-100"
                                style={{ height: `calc((100% - ${labelSpace}px) * ${v / max})`, minHeight: v ? 2 : 0 }} />
                        </div>
                    );
                })}
            </div>
            {xLabel ? (
                <div className="d-flex border-top" style={{ gap: 2 }}>
                    {data.map(d => (
                        <div key={d[labelKey]} className="flex-fill text-center text-muted" style={{ minWidth: 0, fontSize: 10 }}>
                            {xLabel(d[labelKey])}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export const bucketUnit = (days) => days === 'all' ? 'month' : days > 31 ? 'week' : 'day';

const bucketKey = (d, unit) => {
    if (unit === 'month') return d.toISOString().slice(0, 7);
    if (unit === 'week') {
        const monday = new Date(d);
        monday.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
        return monday.toISOString().slice(0, 10);
    }
    return d.toISOString().slice(0, 10);
};

// Group daily rows into day/week/month buckets and fill gaps so the x-axis is continuous
export const bucketDaily = (rows, days) => {
    const unit = bucketUnit(days);
    const totals = {};
    rows.forEach(r => {
        const k = bucketKey(new Date(`${r.date}T00:00:00Z`), unit);
        totals[k] = (totals[k] || 0) + (Number(r.views) || 0);
    });
    const now = new Date();
    const end = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const start = days === 'all'
        ? (rows.length ? new Date(`${rows[0].date}T00:00:00Z`) : end)
        : new Date(end.getTime() - (days - 1) * 86400000);
    const out = [];
    const seen = new Set();
    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const k = bucketKey(d, unit);
        if (!seen.has(k)) {
            seen.add(k);
            out.push({ date: k, views: totals[k] || 0 });
        }
    }
    return out;
};

export const fillHourly = (rows) => {
    const byHour = Object.fromEntries(rows.map(r => [r.hour, r]));
    return Array.from({ length: 24 }, (_, h) => byHour[h] || { hour: h, views: 0 });
};

const SceneHistoryDashboard = () => {
    const [days, setDays] = useState(30);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userFilter, setUserFilter] = useState(null);
    const [siteFilter, setSiteFilter] = useState(null);
    const [userLimit, setUserLimit] = useState(10);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        let cancelled = false;
        const query = new URLSearchParams({ days, userLimit });
        if (userFilter) query.set('user', userFilter);
        if (siteFilter) query.set('site', siteFilter.id);
        setLoading(true);
        axios.get(`${constants.USER_API.USER_API_URL}/admin/history/scenes-stats?${query}`, {
            headers: { 'Authorization': token }
        }).then(res => { if (!cancelled) setStats(res.data); })
            .catch(err => console.log(err))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [days, userFilter, siteFilter, userLimit]);

    const selectUser = (username) => setUserFilter(f => f === username ? null : username);
    const selectSite = (site) => {
        if (site.siteId) setSiteFilter(f => f?.id === site.siteId ? null : { id: site.siteId, name: site.site });
    };

    const t = stats?.totals || {};
    const unit = bucketUnit(days);
    const daily = stats ? bucketDaily(stats.daily, days) : [];
    const hourly = stats ? fillHourly(stats.hourly) : [];
    const avgPerView = t.views ? t.watchSeconds / t.views : 0;

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">Scene History {loading ? <Spinner animation="border" size="sm" /> : null}</h3>
                <ButtonGroup size="sm">
                    {RANGES.map(r => (
                        <Button key={r} variant={r === days ? 'primary' : 'outline-primary'} onClick={() => setDays(r)}>
                            {r === 'all' ? 'All time' : `${r}d`}
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            {userFilter || siteFilter ? (
                <div className="mb-3">
                    <span className="text-muted small mr-2">Breakdown for</span>
                    {userFilter ? (
                        <Button size="sm" variant="info" className="mr-2" onClick={() => setUserFilter(null)}>
                            User: {userFilter} &times;
                        </Button>
                    ) : null}
                    {siteFilter ? (
                        <Button size="sm" variant="info" className="mr-2" onClick={() => setSiteFilter(null)}>
                            Site: {siteFilter.name} &times;
                        </Button>
                    ) : null}
                    <Button size="sm" variant="link" onClick={() => { setUserFilter(null); setSiteFilter(null); }}>Clear</Button>
                </div>
            ) : null}

            <Row className="mb-3">
                <Col xs={6} lg={3} className="mb-2"><Kpi label="Views" value={t.views ?? '-'} /></Col>
                <Col xs={6} lg={3} className="mb-2"><Kpi label="Viewers" value={t.viewers ?? '-'} /></Col>
                <Col xs={6} lg={3} className="mb-2"><Kpi label="Unique scenes" value={t.scenes ?? '-'} /></Col>
                <Col xs={6} lg={3} className="mb-2">
                    <Kpi label="Watch time" value={formatDuration(t.watchSeconds)} sub={`avg ${formatDuration(avgPerView)} / view`} />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col lg={8} className="mb-2">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>{unit === 'day' ? 'Daily' : unit === 'week' ? 'Weekly' : 'Monthly'} views</Card.Header>
                        <Card.Body>
                            <BarChart data={daily} labelKey="date" valueKey="views"
                                formatLabel={k => unit === 'week' ? `Week of ${k}` : k}
                                xLabel={daily.length <= 31 ? (k => unit === 'month' ? k.slice(2) : k.slice(8)) : null} />
                            <div className="d-flex justify-content-between text-muted small mt-1">
                                <span>{daily[0]?.date}</span><span>{daily[daily.length - 1]?.date}</span>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4} className="mb-2">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>Views by hour</Card.Header>
                        <Card.Body>
                            <BarChart data={hourly} labelKey="hour" valueKey="views" formatLabel={h => `${h}:00`} xLabel={h => h} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col lg={6} className="mb-3">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>Top scenes</Card.Header>
                        <Table size="sm" hover responsive className="mb-0">
                            <tbody>
                                {(stats?.topScenes || []).map(s => (
                                    <tr key={s.id}>
                                        <td style={{ width: 80 }}>
                                            <img src={scenePoster(s.id)} alt="" style={{ width: 72, height: 40, objectFit: 'cover' }} />
                                        </td>
                                        <td>
                                            <Link to={`/scene/${s.id}`}>{s.title || s.id}</Link>
                                            <div className="text-muted small">{s.site}</div>
                                        </td>
                                        <td className="text-right text-nowrap">
                                            <strong>{s.views}</strong> views
                                            <div className="text-muted small">{s.viewers} viewers</div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>Top users</Card.Header>
                        <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                            <Table size="sm" hover className="mb-0">
                                <tbody>
                                    {(stats?.topUsers || []).map((u, i) => (
                                        <tr key={u.username || i} onClick={() => u.username && selectUser(u.username)}
                                            className={u.username === userFilter ? 'table-info' : ''} style={{ cursor: 'pointer' }}>
                                            <td className="text-muted small" style={{ width: 24 }}>{i + 1}</td>
                                            <td>{u.username}<div className="text-muted small">{formatDuration(u.watchSeconds)}</div></td>
                                            <td className="text-right">{u.views}<div className="text-muted small">{u.scenes} scenes</div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {(stats?.topUsers || []).length >= userLimit && !userFilter ? (
                            <Card.Footer className="text-center py-1">
                                <Button size="sm" variant="link" onClick={() => setUserLimit(l => l + 25)}>Show more</Button>
                            </Card.Footer>
                        ) : null}
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>Top sites</Card.Header>
                        <Card.Body className="py-2">
                            {(stats?.topSites || []).map(s => {
                                const pct = t.views ? (s.views / t.views) * 100 : 0;
                                return (
                                    <div key={s.siteId ?? s.site} className={`mb-2 px-1 rounded ${siteFilter?.id === s.siteId ? 'bg-light' : ''}`}
                                        onClick={() => selectSite(s)} style={{ cursor: s.siteId ? 'pointer' : 'default' }}>
                                        <div className="d-flex justify-content-between small">
                                            <span className="text-truncate">{s.site}</span><span>{s.views}</span>
                                        </div>
                                        <div className="progress" style={{ height: 6 }}>
                                            <div className="progress-bar" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SceneHistoryDashboard;
